import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { parseError } from '../../../../lib/errors'
import { useUploadPolicy } from '../../../../models/coiGenerator'

interface UploadPolicyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UploadPolicyDialog({ open, onOpenChange }: UploadPolicyDialogProps) {
  const [files, setFiles] = useState<File[]>([])
  const uploadPolicy = useUploadPolicy()

  const handleUpload = async () => {
    if (files.length === 0) return
    try {
      await uploadPolicy.mutateAsync(files)
    } catch (err) {
      // Without this the dialog just sits there on a failed upload: the throw
      // skips the close below, and nothing tells you the request 500'd.
      toast.error(parseError(err))
      return
    }
    onOpenChange(false)
    setFiles([])
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Policy Documents</DialogTitle>
          <DialogDescription>
            Upload the policy PDFs to test COI generation. Add more than one if the policy came as several
            documents, such as a declaration page plus a cargo schedule — they are parsed together and merged
            onto one certificate.
          </DialogDescription>
        </DialogHeader>
        <Dropzone multiple onMultipleChange={setFiles} accept=".pdf" isLoading={uploadPolicy.isPending} />
        {files.length > 0 && (
          <ul className="text-md text-muted-foreground list-inside list-disc">
            {files.map((file) => (
              <li key={file.name}>{file.name}</li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploadPolicy.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || uploadPolicy.isPending}
            loading={uploadPolicy.isPending ? 'Uploading...' : undefined}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
